import { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '@/lib/auth';
import { Button } from '@/components/ui/button';
import { Sheet, SheetContent, SheetTrigger, SheetTitle } from '@/components/ui/sheet';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { Menu, Dog, User, LogOut, LayoutDashboard, PlusCircle, Heart, ChevronDown, Info, Phone, HelpCircle } from 'lucide-react';

const navLinks = [
  { label: 'Home', path: '/' },
  { label: 'Marketplace', path: '/dogs-for-sale' },
  { label: 'Breeds', path: '/dog-breeds' },
  { label: 'Dog Matcher', path: '/dog-matcher' },
  { label: 'Blog', path: '/blog' },
];

const moreLinks = [
  { label: 'About Us', path: '/about', icon: Info },
  { label: 'Contact', path: '/contact', icon: Phone },
  { label: 'FAQ', path: '/faq', icon: HelpCircle },
];

export function Navbar() {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);

  const isActive = (path) => {
    if (path === '/') return location.pathname === '/';
    return location.pathname.startsWith(path);
  };

  const isMoreActive = moreLinks.some(l => location.pathname.startsWith(l.path));

  return (
    <header className="sticky top-0 z-50 w-full bg-white/80 backdrop-blur-xl border-b border-gray-100" data-testid="main-navbar">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2 shrink-0" data-testid="logo-link">
            <div className="w-9 h-9 rounded-xl bg-[#4A90E2] flex items-center justify-center">
              <Dog className="w-5 h-5 text-white" strokeWidth={2} />
            </div>
            <span className="font-['Nunito'] font-extrabold text-xl text-[#1A1D20] tracking-tight">
              DoggoHub
            </span>
          </Link>

          {/* Desktop Nav */}
          <nav className="hidden md:flex items-center gap-1">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className={`nav-link px-3 py-2 text-sm font-medium rounded-lg transition-colors ${
                  isActive(link.path) ? 'text-[#4A90E2] bg-blue-50/60' : 'text-[#5C636A] hover:text-[#1A1D20] hover:bg-gray-50'
                }`}
                data-testid={`nav-${link.label.toLowerCase().replace(/\s/g, '-')}`}
              >
                {link.label}
              </Link>
            ))}

            {/* More Dropdown */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  className={`nav-link px-3 py-2 text-sm font-medium rounded-lg transition-colors flex items-center gap-1 ${
                    isMoreActive ? 'text-[#4A90E2] bg-blue-50/60' : 'text-[#5C636A] hover:text-[#1A1D20] hover:bg-gray-50'
                  }`}
                  data-testid="nav-more-dropdown"
                >
                  More <ChevronDown className="w-3.5 h-3.5" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="center" className="w-44">
                {moreLinks.map((link) => (
                  <DropdownMenuItem key={link.path} onClick={() => navigate(link.path)} data-testid={`nav-more-${link.label.toLowerCase().replace(/\s/g, '-')}`}>
                    <link.icon className="w-4 h-4 mr-2 text-[#5C636A]" /> {link.label}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          </nav>

          {/* Desktop Actions */}
          <div className="hidden md:flex items-center gap-3">
            {user ? (
              <>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => navigate('/favorites')}
                  className="rounded-full"
                  data-testid="nav-favorites-button"
                >
                  <Heart className={`w-5 h-5 ${isActive('/favorites') ? 'text-red-500 fill-red-500' : 'text-[#5C636A]'}`} />
                </Button>
                <Button
                  onClick={() => navigate('/dashboard/new')}
                  className="rounded-full bg-[#F5A623] hover:bg-[#e09515] text-white font-semibold shadow-[0_4px_14px_rgb(245,166,35,0.4)] cta-button"
                  data-testid="post-ad-button"
                >
                  <PlusCircle className="w-4 h-4 mr-1" />
                  Post Ad
                </Button>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" size="icon" className="rounded-full" data-testid="user-menu-trigger">
                      <div className="w-8 h-8 rounded-full bg-[#4A90E2] flex items-center justify-center text-white text-sm font-bold">
                        {user.name?.charAt(0)?.toUpperCase() || 'U'}
                      </div>
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-48">
                    <div className="px-2 py-1.5">
                      <p className="text-sm font-medium">{user.name}</p>
                      <p className="text-xs text-muted-foreground">{user.email}</p>
                    </div>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onClick={() => navigate('/dashboard')} data-testid="menu-dashboard">
                      <LayoutDashboard className="w-4 h-4 mr-2" /> Dashboard
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => navigate('/favorites')} data-testid="menu-favorites">
                      <Heart className="w-4 h-4 mr-2" /> Favorites
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onClick={logout} data-testid="menu-logout">
                      <LogOut className="w-4 h-4 mr-2" /> Sign Out
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </>
            ) : (
              <Button
                onClick={() => navigate('/auth')}
                className="rounded-full bg-[#4A90E2] hover:bg-[#3a7bc8] text-white font-semibold"
                data-testid="sign-in-button"
              >
                <User className="w-4 h-4 mr-1" />
                Sign In
              </Button>
            )}
          </div>

          {/* Mobile Menu */}
          <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
            <SheetTrigger asChild className="md:hidden">
              <Button variant="ghost" size="icon" data-testid="mobile-menu-trigger">
                <Menu className="w-5 h-5" />
              </Button>
            </SheetTrigger>
            <SheetContent side="right" className="w-72 pt-12">
              <SheetTitle className="sr-only">Navigation Menu</SheetTitle>
              <nav className="flex flex-col gap-2">
                {navLinks.map((link) => (
                  <Link
                    key={link.path}
                    to={link.path}
                    onClick={() => setMobileOpen(false)}
                    className={`px-4 py-3 text-sm font-medium rounded-xl transition-colors ${
                      isActive(link.path) ? 'text-[#4A90E2] bg-blue-50' : 'text-[#5C636A] hover:bg-gray-50'
                    }`}
                    data-testid={`mobile-nav-${link.label.toLowerCase().replace(/\s/g, '-')}`}
                  >
                    {link.label}
                  </Link>
                ))}
                <div className="border-t my-2" />
                <p className="px-4 text-xs font-semibold text-[#5C636A] uppercase tracking-wide">More</p>
                {moreLinks.map((link) => (
                  <Link
                    key={link.path}
                    to={link.path}
                    onClick={() => setMobileOpen(false)}
                    className={`px-4 py-3 text-sm font-medium rounded-xl transition-colors flex items-center gap-2 ${
                      isActive(link.path) ? 'text-[#4A90E2] bg-blue-50' : 'text-[#5C636A] hover:bg-gray-50'
                    }`}
                    data-testid={`mobile-nav-${link.label.toLowerCase().replace(/\s/g, '-')}`}
                  >
                    <link.icon className="w-4 h-4" /> {link.label}
                  </Link>
                ))}
                <div className="border-t my-2" />
                {user ? (
                  <>
                    <Link
                      to="/favorites"
                      onClick={() => setMobileOpen(false)}
                      className="px-4 py-3 text-sm font-medium rounded-xl hover:bg-gray-50 flex items-center gap-2 text-red-500"
                      data-testid="mobile-favorites"
                    >
                      <Heart className="w-4 h-4" /> Favorites
                    </Link>
                    <Link
                      to="/dashboard/new"
                      onClick={() => setMobileOpen(false)}
                      className="px-4 py-3 text-sm font-semibold text-[#F5A623] rounded-xl hover:bg-orange-50"
                      data-testid="mobile-post-ad"
                    >
                      Post Ad
                    </Link>
                    <Link
                      to="/dashboard"
                      onClick={() => setMobileOpen(false)}
                      className="px-4 py-3 text-sm font-medium rounded-xl hover:bg-gray-50"
                      data-testid="mobile-dashboard"
                    >
                      Dashboard
                    </Link>
                    <button
                      onClick={() => { logout(); setMobileOpen(false); }}
                      className="px-4 py-3 text-sm font-medium text-left rounded-xl hover:bg-gray-50 text-red-500"
                      data-testid="mobile-logout"
                    >
                      Sign Out
                    </button>
                  </>
                ) : (
                  <Link
                    to="/auth"
                    onClick={() => setMobileOpen(false)}
                    className="px-4 py-3 text-sm font-semibold text-[#4A90E2] rounded-xl hover:bg-blue-50"
                    data-testid="mobile-sign-in"
                  >
                    Sign In
                  </Link>
                )}
              </nav>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </header>
  );
}
